/**
 * Delbot Mouse Analysis Integration
 * Uses CDN-loaded Delbot library for mouse pattern detection
 */

export interface DelbotResult {
  human?: boolean;
  confidence?: number;
}

// Global types for Delbot from CDN
declare global {
  interface Window {
    Delbot?: {
      Recorder: unknown;
      Models: unknown;
    };
    DELBOT?: {
      Recorder: unknown;
      Models: unknown;
    };
    delbot?: {
      Recorder: unknown;
      Models: unknown;
    };
  }
}

// Delbot Recorder wrapper
class DelbotRecorder {
  private recorder: unknown;
  private ready: Promise<void>;

  constructor(width: number, height: number) {
    this.ready = new Promise((resolve, reject) => {
      const checkReady = (attempts = 0) => {
        const delbotGlobal = window.delbot || window.Delbot || window.DELBOT;

        if (delbotGlobal && delbotGlobal.Recorder) {
          const RecorderClass = delbotGlobal.Recorder as new (width: number, height: number) => unknown;
          this.recorder = new RecorderClass(width, height);
          resolve();
        } else if (attempts < 50) {
          setTimeout(() => checkReady(attempts + 1), 100);
        } else {
          reject(new Error('Delbot library not loaded from CDN'));
        }
      };

      if (typeof window !== 'undefined') {
        checkReady();
      } else {
        reject(new Error('Window is not defined'));
      }
    });
  }

  async setMaxSize(size: number) {
    await this.ready;
    return (this.recorder as any)?.setMaxSize(size);
  }

  async addRecord(record: { time: number; x: number; y: number; type: string }) {
    await this.ready;
    return (this.recorder as any)?.addRecord(record);
  }

  async isHuman(model: unknown) {
    await this.ready;
    return (this.recorder as any)?.isHuman(model);
  }

  async getRecords() {
    await this.ready;
    return (this.recorder as any)?.getRecords() || [];
  }

  async clearRecord() {
    await this.ready;
    return (this.recorder as any)?.clearRecord();
  }
}

// Get Delbot Models
async function getModels() {
  return new Promise((resolve, reject) => {
    let attempts = 0;

    const checkModels = () => {
      const delbotGlobal = window.delbot || window.Delbot || window.DELBOT;

      if (delbotGlobal && delbotGlobal.Models) {
        resolve(delbotGlobal.Models);
      } else if (attempts < 50) {
        attempts++;
        setTimeout(checkModels, 100);
      } else {
        reject(new Error('Delbot Models not loaded from CDN'));
      }
    };

    if (typeof window !== 'undefined') {
      checkModels();
    } else {
      reject(new Error('Window is not defined'));
    }
  });
}

// Track mouse movements for Delbot
let recorder: DelbotRecorder | null = null;
let mouseTrackingStarted = false;

function startMouseTracking() {
  if (mouseTrackingStarted || typeof window === 'undefined') return;

  recorder = new DelbotRecorder(window.innerWidth, window.innerHeight);
  mouseTrackingStarted = true;

  document.addEventListener('mousemove', (e: MouseEvent) => {
    recorder?.addRecord({
      time: Date.now(),
      x: e.clientX,
      y: e.clientY,
      type: 'move',
    });
  });

  document.addEventListener('click', (e: MouseEvent) => {
    recorder?.addRecord({
      time: Date.now(),
      x: e.clientX,
      y: e.clientY,
      type: 'click',
    });
  });
}

/**
 * Analyze mouse patterns with Delbot
 */
export async function detectWithDelbot(): Promise<DelbotResult> {
  try {
    // Start tracking if not started
    if (!mouseTrackingStarted) {
      startMouseTracking();
    }

    // Wait a bit for some mouse data (or use existing data)
    if (!recorder) {
      return {};
    }

    const records = await recorder.getRecords();

    // Need at least 10 mouse movements for analysis
    if (!records || records.length < 10) {
      return {};
    }

    // Get Delbot model
    const models = await getModels();
    const isHuman = await recorder.isHuman(models);

    // Delbot returns a probability, convert to our format
    const confidence = typeof isHuman === 'number' ? isHuman : isHuman ? 0.9 : 0.1;

    return {
      human: confidence > 0.5,
      confidence,
    };
  } catch (error) {
    // Delbot not available or error - fail gracefully
    return {};
  }
}

export { startMouseTracking };
